import {Chip, Stack, Text} from '@mantine/core';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {StoichiometryCalculationMode} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import type {
  StoichiometryCreateGroupExtras,
  StoichiometryGroupMetadata,
  StoichiometryNoteLinkedGroupAdapter,
} from 'notebook/integrations/chemistry/StoichiometryNoteLinkedGroupAdapter';
import makeSimpleGroupActionFunction from 'notebook/notes/groups/action-functions/makeSimpleGroupActionFunction';
import {isStoichiometryGroup} from 'notebook/TypePredicates';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectableCard from 'ui-library/inputs/SelectableCard';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';

const MODE_DETAILS = {
  [StoichiometryCalculationMode.STANDARD]: {
    title: 'Standard',
    description: 'Calculated values keep the maximum precision with no rounding.',
    example: '200.00mol / 3.00M = 66.6666666666667L',
  },
  [StoichiometryCalculationMode.SIGFIGS]: {
    title: 'Significant figures (default)',
    description:
      'Calculated values are rounded to match the fewest number of significant figures from inputted measurements.',
    example: '200.00mol / 3.00M = 66.7L',
  },
} satisfies Record<StoichiometryCalculationMode, {title: string; description: string; example: string}>;

export interface StoichiometryCalculationSettingsModalValues {
  calculationMode: StoichiometryCalculationMode;
}

interface StoichiometryCalculationSettingsModalProps {
  calculationMode: StoichiometryCalculationMode;
  locked: boolean;
  onRequestClose: () => void;
  onRequestSubmit: (values: StoichiometryCalculationSettingsModalValues) => void;
}

/**
 * Settings for Reaction table calculation precision.
 */
export default function StoichiometryCalculationSettingsModal(
  props: StoichiometryCalculationSettingsModalProps
): JSX.Element {
  const [calculationMode, setCalculationMode] = useState(props.calculationMode);

  return (
    <Modal
      title="Calculation precision"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit({calculationMode});
      }}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Save',
          disabled: props.locked,
          style: ButtonStyle.SECONDARY,
          submit: true,
        },
      ]}
    >
      <Stack spacing="sm">
        <Text>Configure how decimal values should be calculated in Reaction tables.</Text>
        <Chip.Group
          multiple={false}
          value={calculationMode}
          onChange={(value) => {
            setCalculationMode(value as StoichiometryCalculationMode);
          }}
        >
          <Stack aria-label="Calculation precision" role="radiogroup" spacing="sm">
            {Object.values(StoichiometryCalculationMode).map((mode) => {
              const {title, description, example} = MODE_DETAILS[mode];
              return (
                <SelectableCard
                  key={mode}
                  value={mode}
                  title={title}
                  subtitle={description}
                  description={`Example: ${example}`}
                  disabled={props.locked}
                />
              );
            })}
          </Stack>
        </Chip.Group>
      </Stack>
    </Modal>
  );
}

/**
 * Opens calculation precision settings for a stoichiometry note-linked group and updates its table.
 */
export const updateStoichiometryCalculationSettingsActionFunction = makeSimpleGroupActionFunction<
  StoichiometryGroupMetadata,
  StoichiometryCreateGroupExtras
>({
  label: 'Calculation precision',
  icon: 'calculator-simple',
  onInvoke: async ({
    noteLinkedGroup,
    noteLinkedGroupAdapter,
    locked,
    getItemWithUpdatedContent,
    updateGroup,
  }) => {
    assert(
      isStoichiometryGroup(noteLinkedGroup),
      'Stoichiometry calculation settings action can only be used with stoichiometry groups'
    );
    const reactionTableNotePart = (
      noteLinkedGroupAdapter as StoichiometryNoteLinkedGroupAdapter
    ).getReactionStructuredTableNotePart(noteLinkedGroup);
    const reactionTable = reactionTableNotePart.item;
    const updatedCalculationSettings = await legacyRunModal(StoichiometryCalculationSettingsModal, {
      calculationMode: reactionTable.metadata.calculationMode ?? StoichiometryCalculationMode.STANDARD,
      locked,
    });
    if (updatedCalculationSettings == null) {
      return;
    }
    const updatedReactionTable = getItemWithUpdatedContent(reactionTableNotePart, {
      ...reactionTableNotePart,
      item: {
        ...reactionTable,
        metadata: {
          ...reactionTable.metadata,
          calculationMode: updatedCalculationSettings.calculationMode,
        },
      },
    });
    const updatedGroup = await noteLinkedGroupAdapter.reduceGroup(noteLinkedGroup, updatedReactionTable);
    updateGroup(updatedGroup);
  },
  getTooltip: () => 'Calculation precision',
  // the modal can be opened, but not edited if the table is locked
  getDisabled: () => false,
});
